/* Editing a post, a Prayer Request or a comment.
 *
 * ONE DIALOG FOR THE THREE, because they differ in exactly two ways: whether
 * photos can be attached and whether a feeling can be. Both are props. Three
 * near-identical dialogs is how a fix to one of them stops reaching the others.
 *
 * SAVE IS DISABLED UNTIL SOMETHING CHANGES. Not pedantry: a Save that is live
 * when nothing has been typed invites a pointless round trip and a pointless
 * "edited" mark, and an "edited" that means "somebody opened this and closed it
 * again" is worse than no mark at all.
 *
 * THE AUDIENCE IS NOT EDITABLE, and that is deliberate — somebody may have
 * replied to a post BECAUSE it was in their group, and quietly widening or
 * narrowing that afterwards changes who was ever able to answer.
 */
import { useEffect, useRef, useState } from 'react'
import { Smile, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { useFeelings } from '@/api/queries'
import { cn } from '@/lib/utils'
import {
  MAX_ATTACHMENTS,
  PhotoButton,
  PhotoGrid,
  usePhotoAttachments,
} from './PhotoPicker'
import type { Media } from '@/types/api'

const MAX = 5000

/** What Save hands back.
 *
 *  `mediaIds` is the FULL list the post ends up with, not an addition — an
 *  editor that can only append gives no way to take a photo off, which is the
 *  half that matters most. `clearFeeling` exists because "remove the feeling"
 *  and "do not touch the feeling" are both null on the wire otherwise.
 */
export interface EditSubmission {
  body: string
  mediaIds: string[]
  feeling: string | null
  clearFeeling: boolean
}

export function EditDialog({
  title,
  initialBody,
  /** Omitted for a Prayer Request and a comment — neither can gain a photo
   *  here, and offering a button that leads nowhere is worse than absent. */
  initialPhotos,
  initialFeeling,
  placeholder = 'What would you like to say?',
  saving,
  error,
  onSave,
  onClose,
}: {
  title: string
  initialBody: string
  initialPhotos?: Media[]
  initialFeeling?: string | null
  placeholder?: string
  saving: boolean
  error?: string | null
  onSave: (edit: EditSubmission) => void
  onClose: () => void
}) {
  const [body, setBody] = useState(initialBody)
  const textarea = useRef<HTMLTextAreaElement>(null)

  const supportsMedia = Boolean(initialPhotos)
  /* TWO SOURCES, ONE ORDER. A photo already on the post is a URL the server
     holds; a newly picked one is a file still going up. They are kept apart
     because only the second needs an upload, but they are SAVED as one list —
     the order the author sees is the order that is stored. */
  const [kept, setKept] = useState<Media[]>(initialPhotos ?? [])
  // The kept ones count against the cap, or a post with three photos could
  // take four more.
  const added = usePhotoAttachments(kept.length)

  const feelings = useFeelings()
  const [feeling, setFeeling] = useState<string | null>(initialFeeling ?? null)
  const [pickingFeeling, setPickingFeeling] = useState(false)
  const chosen = feelings.data?.find((f) => f.kind === feeling) ?? null

  // Grow with the text rather than scrolling inside a fixed box.
  useEffect(() => {
    const el = textarea.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 260)}px`
  }, [body])

  const photoCount = kept.length + added.items.length
  /* A kept video fills the post on its own. */
  const keptVideo = kept.some((m) => m.kind === 'video')

  const changed =
    body.trim() !== initialBody.trim() ||
    feeling !== (initialFeeling ?? null) ||
    kept.length !== (initialPhotos?.length ?? 0) ||
    added.items.length > 0

  // A post may be a picture on its own, exactly as when it was written — so
  // "empty" means no words AND no photos.
  const hasContent =
    Boolean(body.trim()) || (supportsMedia && (kept.length > 0 || added.hasAny))

  function submit() {
    // Saving mid-upload would silently drop the photo still in flight.
    if (!changed || !hasContent || saving || added.isUploading) return
    onSave({
      body: body.trim(),
      mediaIds: [...kept.map((m) => m.id), ...added.mediaIds],
      feeling,
      clearFeeling: feeling === null,
    })
  }

  return (
    <Modal
      open
      onOpenChange={(next) => {
        if (!next) {
          added.reset()
          onClose()
        }
      }}
      title={title}
      // A stray click outside must not throw away a half-written correction.
      guardClose={changed}
      footer={
        <Button
          variant="primary"
          size="lg"
          full
          onClick={submit}
          disabled={!changed || !hasContent || added.isUploading}
          loading={saving}
        >
          {added.videoProgress !== null
            ? `Uploading video ${Math.round(added.videoProgress * 100)}%`
            : added.isUploading
              ? 'Uploading…'
              : 'Save'}
        </Button>
      }
    >
      <div className="space-y-s3 p-s4">
        <textarea
          ref={textarea}
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, MAX))}
          placeholder={placeholder}
          aria-label={title}
          rows={3}
          autoFocus
          className="w-full resize-none border-none bg-transparent text-lg outline-none placeholder:text-ink-2"
        />

        {supportsMedia && pickingFeeling ? (
          <div className="rounded-md border border-line-2 p-s3">
            <div className="mb-s2 flex items-center justify-between">
              <span className="text-sm font-bold text-ink-2">
                How are you feeling?
              </span>
              {chosen ? (
                <button
                  type="button"
                  onClick={() => {
                    setFeeling(null)
                    setPickingFeeling(false)
                  }}
                  className="text-sm font-semibold text-brand hover:underline"
                >
                  Clear
                </button>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-s2">
              {feelings.data?.map((f) => (
                <button
                  key={f.kind}
                  type="button"
                  onClick={() => {
                    // Pressing the chosen one again clears it, so there is no
                    // way to get stuck with a feeling you cannot remove.
                    setFeeling(feeling === f.kind ? null : f.kind)
                    setPickingFeeling(false)
                  }}
                  className={cn(
                    'rounded-pill border px-s3 py-1 text-base transition-colors',
                    feeling === f.kind
                      ? 'border-warning bg-warning-soft font-semibold text-warning-ink'
                      : 'border-line-2 hover:bg-hover',
                  )}
                >
                  <span aria-hidden className="mr-1">{f.emoji}</span>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {/* The photos already on the post. Removable — that is the whole
            reason media_ids is the full list rather than an addition. */}
        {kept.length ? (
          <div
            className={cn(
              'grid gap-s2',
              kept.length === 1 ? 'grid-cols-1' : 'grid-cols-2',
            )}
          >
            {kept.map((photo) => (
              <div
                key={photo.id}
                className="relative overflow-hidden rounded bg-surface-2"
              >
                {/* A kept video shows its poster frame: editing is about
                    what stays on the post, not about watching it again. */}
                <img
                  src={(photo.kind === 'video' ? photo.poster_url : photo.url) ?? undefined}
                  alt=""
                  className={cn(
                    'w-full object-cover',
                    kept.length === 1 ? 'max-h-[320px]' : 'h-[150px]',
                  )}
                />
                <button
                  type="button"
                  onClick={() =>
                    setKept((prev) => prev.filter((p) => p.id !== photo.id))
                  }
                  aria-label="Remove photo"
                  className="absolute right-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-pill bg-black/60 text-white transition-colors hover:bg-black/80"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        ) : null}

        {supportsMedia ? (
          <>
            {/* Newly picked ones, with their own upload state. */}
            <PhotoGrid items={added.items} onRemove={added.remove} />

            <div className="flex items-center gap-s2">
              {/* A post keeps photos OR one video. With photos already on
                  it, only more photos; with a video, nothing beside it. */}
              <PhotoButton
                onPick={added.add}
                disabled={added.isFull || keptVideo}
                accept={photoCount ? 'image/*' : 'image/*,video/*'}
                label={
                  keptVideo
                    ? 'One video per post'
                    : added.isFull
                      ? `Up to ${MAX_ATTACHMENTS} photos`
                      : photoCount
                        ? 'Add another photo'
                        : 'Add photos or a video'
                }
              />
              <button
                type="button"
                onClick={() => setPickingFeeling((v) => !v)}
                aria-expanded={pickingFeeling}
                className={cn(
                  'flex items-center gap-1 rounded-pill px-s2 py-1 text-sm font-semibold transition-colors',
                  chosen
                    ? 'bg-warning-soft text-warning-ink'
                    : 'text-ink-2 hover:bg-hover',
                )}
              >
                <Smile className="h-[18px] w-[18px] text-warning" aria-hidden />
                {chosen ? `feeling ${chosen.label}` : 'Feeling'}
              </button>
            </div>

          </>
        ) : null}

        {error ? (
          <p className="text-sm font-semibold text-danger">{error}</p>
        ) : null}

        {/* Only once it starts to matter — a counter at 4,800 remaining is
            noise. */}
        {MAX - body.length <= 500 ? (
          <p
            className={cn(
              'text-right text-sm',
              MAX - body.length < 100 ? 'text-danger' : 'text-ink-2',
            )}
          >
            {MAX - body.length}
          </p>
        ) : null}
      </div>
    </Modal>
  )
}
